export function formatCalendarCellDate(dateInput) {
    const d = new Date(dateInput);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function getMonthKeyFromDateValue(dateInput) {
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return `${date.getFullYear()}-${date.getMonth()}`;
}

export function getSafeDisplayDate(value) {
    if (!value) return null;

    if (value instanceof Date) {
        const parsed = new Date(value.getFullYear(), value.getMonth(), value.getDate());
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const stringValue = String(value).trim();
    const parts = stringValue.split("-");

    if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        const parsed = new Date(year, month - 1, day);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(stringValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getLocalDateString(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return "";
    }

    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
}

export function getWeekKeyFromDate(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return null;
    }

    const dayOfWeek = parsed.getDay();
    const diff = parsed.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(parsed);
    monday.setDate(diff);

    return `${monday.getFullYear()}-${monday.getMonth()}-${monday.getDate()}`;
}

export function getMondayForDate(value) {
    const parsed = getSafeDisplayDate(value);

    if (!parsed) {
        return null;
    }

    const dayOfWeek = parsed.getDay();
    const diff = parsed.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(parsed);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);

    return monday;
}

export function getMonthGridStartDate(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    return getMondayForDate(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
}

export function getMonthGridEndDate(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    const monthGridStart = getMonthGridStartDate(monthDate);
    if (!monthGridStart) {
        return null;
    }

    const gridEnd = new Date(monthGridStart);
    gridEnd.setDate(monthGridStart.getDate() + 41);
    gridEnd.setHours(0, 0, 0, 0);

    return gridEnd;
}

export function getFirstMondayWithinMonth(monthDateValue) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return null;
    }

    const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const firstMonday = getMondayForDate(firstDayOfMonth);

    if (!firstMonday) {
        return null;
    }

    if (firstMonday.getMonth() !== monthDate.getMonth()) {
        firstMonday.setDate(firstMonday.getDate() + 7);
    }

    firstMonday.setHours(0, 0, 0, 0);
    return firstMonday;
}

export function buildMonthWeekStartDates(monthDateValue, options = {}) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return [];
    }

    const today = getSafeDisplayDate(options.today ?? new Date());
    const maxWeeks = Number(options.maxWeeks) > 0 ? Number(options.maxWeeks) : 6;
    const monthGridStart = getMonthGridStartDate(monthDate);
    const monthGridEnd = getMonthGridEndDate(monthDate);

    if (!monthGridStart || !monthGridEnd) {
        return [];
    }

    const isCurrentMonth = !!today &&
        today.getFullYear() === monthDate.getFullYear() &&
        today.getMonth() === monthDate.getMonth();

    const currentWeekMonday = today ? getMondayForDate(today) : null;
    let startWeek = monthGridStart;
    let endWeek = getMondayForDate(monthGridEnd) || monthGridEnd;

    if (isCurrentMonth && currentWeekMonday && currentWeekMonday > monthGridStart) {
        // Start at the current week rather than the grid's first row: nobody needs the
        // weeks they have already lived through.
        //
        // The END is deliberately NOT clamped to the month. It used to be
        // `getMondayForDate(endOfMonth)`, which for September 2026 is 28 Sep - but
        // FullCalendar renders a fixed six rows (`fixedWeekCount` defaults to true), so
        // the grid's last row starts 5 Oct. That row was rendered and clickable while
        // never appearing in this list, so it was never fetched: bare cells, and a click
        // that only reached the bare-cell handler instead of opening a popup.
        startWeek = currentWeekMonday;
    }

    const weekStarts = [];
    const cursor = new Date(startWeek);

    for (let index = 0; index < maxWeeks && cursor <= endWeek; index += 1) {
        weekStarts.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 7);
    }

    return weekStarts;
}

export function buildMonthWeekData(monthDateValue, scheduleItems = []) {
    const monthDate = getSafeDisplayDate(monthDateValue);

    if (!monthDate) {
        return {};
    }

    const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const monthWeekData = {};

    const cursor = new Date(startOfMonth);
    while (cursor <= endOfMonth) {
        const weekKey = getWeekKeyFromDate(cursor);
        if (weekKey && !monthWeekData[weekKey]) {
            monthWeekData[weekKey] = [];
        }
        cursor.setDate(cursor.getDate() + 1);
    }

    (Array.isArray(scheduleItems) ? scheduleItems : []).forEach((scheduleItem) => {
        const weekKey = getWeekKeyFromDate(scheduleItem?.BookingDate);
        if (!weekKey) {
            return;
        }

        if (!monthWeekData[weekKey]) {
            monthWeekData[weekKey] = [];
        }

        monthWeekData[weekKey].push(scheduleItem);
    });

    return monthWeekData;
}

export function formatDateString(dateString) {
    if (!dateString) {
        return null;
    }

    const date = getSafeDisplayDate(dateString);

    if (!date || Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
}
/**
 * Height of the chrome pinned to the top of the month scroll container: the sticky
 * toolbar plus the sticky `Mon…Sun` header row. A week row sitting under either of
 * them is covered, not visible.
 *
 * Computed from each element's resting offset (its CSS `top`) plus its own height,
 * NOT from its current `getBoundingClientRect().top`. Those differ: an element that
 * has not been scrolled far enough to stick yet still reports its flow position, so a
 * rect-based inset changes as the user scrolls and the spacing it drives visibly
 * shifts. `top + height` is where the element comes to rest once stuck, which is the
 * same number at any scroll position.
 *
 * Read from the elements rather than from `--calendar-toolbar-height` /
 * `--calendar-toolbar-top`, because a second copy of those numbers here would drift
 * silently the moment anyone tuned the CSS.
 *
 * @param {HTMLElement} rootElement calendar root (contains .fc-header-toolbar)
 * @returns {number} pixels of cover at the top of the scroll port
 */
function getStickyTopInset(rootElement) {
    if (!rootElement || typeof window.getComputedStyle !== "function") {
        return 0;
    }

    const stickyParts = [
        rootElement.querySelector(".fc-header-toolbar"),
        // The sticky rule lands on the cell, not the row - FullCalendar's own
        // `.fc-scrollgrid-section-sticky > *`. Measuring the `tr` would report
        // `position: static` and be skipped.
        rootElement.querySelector("tr.fc-scrollgrid-section-header > th"),
    ];

    return stickyParts.reduce((inset, element) => {
        if (!element) {
            return inset;
        }

        const style = window.getComputedStyle(element);

        if (style.position !== "sticky") {
            return inset;
        }

        const restingTop = parseFloat(style.top);

        if (Number.isNaN(restingTop)) {
            return inset;
        }

        return Math.max(inset, restingTop + element.getBoundingClientRect().height);
    }, 0);
}

/**
 * Monday of every week row the user can see any part of — anything overlapping the
 * area between the pinned chrome and the bottom of the scroll container.
 *
 * Partly visible rows count. The manual refresh is driven by "these are the weeks on
 * my screen, bring them up to date", and a row showing three of its seven days is
 * still a row being looked at. Only a row completely hidden behind the sticky toolbar
 * and weekday header, or completely below the fold, is excluded.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {HTMLElement|Window} scrollContainer the month scroll container
 * @returns {Date[]} week-start Dates, top row first; empty when nothing qualifies
 */
export function getVisibleWeekStarts(rootElement, scrollContainer) {
    if (!rootElement) {
        return [];
    }

    const isWindowScroll = !scrollContainer || scrollContainer === window;
    const viewportRect = isWindowScroll
        ? { top: 0, bottom: window.innerHeight }
        : scrollContainer.getBoundingClientRect();

    const topBound = viewportRect.top + getStickyTopInset(rootElement);
    const bottomBound = viewportRect.bottom;

    const rows = Array.from(rootElement.querySelectorAll(".fc-daygrid-body tbody tr"));

    return rows
        .filter((row) => {
            const rect = row.getBoundingClientRect();
            // Any overlap with the usable area counts. A zero-height row (mid-render)
            // would otherwise pass trivially, so it is excluded.
            return rect.height > 0 && rect.bottom > topBound && rect.top < bottomBound;
        })
        .map((row) => row.querySelector(".fc-daygrid-day[data-date]"))
        .map((cell) => getMondayForDate(cell?.getAttribute("data-date")))
        .filter(Boolean);
}
// Class marking a week row as the start of a scroll page. Kept here next to the code
// that applies it; main-calendar.css hangs `scroll-snap-align: start` off it.
export const WEEK_SNAP_POINT_CLASS = "is-week-snap-point";

/**
 * Tag every Nth week row as a scroll snap point, so one scroll gesture advances a
 * whole screenful of weeks instead of landing part-way through a row.
 *
 * N is measured, not assumed: it is how many whole rows fit below the pinned chrome,
 * which is 3 at the container's 685px cap but drops to 2 on a short window. Recompute
 * on resize and whenever the month re-renders.
 *
 * The browser does all the actual scrolling. Nothing here intercepts wheel or touch
 * events - that would fight both native momentum and the overscroll month gesture,
 * which needs those same events.
 *
 * Also sets `scroll-padding-top` on the container to the measured inset. Without it a
 * snapped row aligns to the container's top edge, which is underneath the sticky
 * toolbar and weekday header.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {HTMLElement|Window} scrollContainer the month scroll container
 * @returns {number} rows per page, or 0 when the grid is not measurable yet
 */
export function applyWeekSnapPoints(rootElement, scrollContainer) {
    if (!rootElement) {
        return 0;
    }

    const isWindowScroll = !scrollContainer || scrollContainer === window;
    const viewportRect = isWindowScroll
        ? { top: 0, bottom: window.innerHeight }
        : scrollContainer.getBoundingClientRect();

    const rows = Array.from(rootElement.querySelectorAll(".fc-daygrid-body tbody tr"));

    if (!rows.length) {
        return 0;
    }

    const rowHeight = rows[0].getBoundingClientRect().height;
    const stickyInset = getStickyTopInset(rootElement);
    const usableHeight = (viewportRect.bottom - viewportRect.top) - stickyInset;

    // Mid-render the grid can report zero heights; tagging rows from that would put
    // snap points in the wrong places until something forced a recalculation.
    if (rowHeight <= 0 || usableHeight <= 0) {
        return 0;
    }

    const rowsPerPage = Math.max(1, Math.floor(usableHeight / rowHeight));

    rows.forEach((row, index) => {
        row.classList.toggle(WEEK_SNAP_POINT_CLASS, index % rowsPerPage === 0);
    });

    if (!isWindowScroll && scrollContainer?.style) {
        scrollContainer.style.scrollPaddingTop = `${Math.round(stickyInset)}px`;
    }

    return rowsPerPage;
}
/**
 * The week row element whose Monday matches `weekStartValue`, or null.
 *
 * Used to keep the reader's place across a month change: the week they were looking at
 * is aligned to the top of the new grid rather than jumping to whichever week happens
 * to come first.
 *
 * Returns null when that week is not in the rendered grid, which is a normal outcome,
 * not an error. Month grids overlap at the boundary most of the time - September 2026
 * ends with the row beginning 28 Sep and October's grid starts on that same row - but
 * not when a month ends exactly on a Sunday (January and February 2027), where the next
 * grid starts on a fresh week. Callers fall back to the top of the grid.
 *
 * @param {HTMLElement} rootElement calendar root element
 * @param {Date|string} weekStartValue any date in the wanted week
 * @returns {HTMLElement|null} the `tr` for that week
 */
export function findWeekRowElement(rootElement, weekStartValue) {
    const weekStart = getMondayForDate(weekStartValue);

    if (!rootElement || !weekStart) {
        return null;
    }

    const rows = Array.from(rootElement.querySelectorAll(".fc-daygrid-body tbody tr"));

    return rows.find((row) => {
        const cell = row.querySelector(".fc-daygrid-day[data-date]");
        const rowWeekStart = getMondayForDate(cell?.getAttribute("data-date"));
        return rowWeekStart && rowWeekStart.getTime() === weekStart.getTime();
    }) || null;
}
